import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaAssetResponse, MediaUsageResponse } from '@/core/generated/api';
import { mediaLibraryService } from '@/entities/MediaLibrary/api/mediaLibraryService';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import classes from './MediaLibrary.module.scss';

interface MediaDeleteDialogProps {
    assets: MediaAssetResponse[];
    onClose: () => void;
    onDeleted: () => void;
}

/** Проверяет использование перед удалением выбранных файлов */
export const MediaDeleteDialog = ({ assets, onClose, onDeleted }: MediaDeleteDialogProps) => {
    const [usage, setUsage] = useState<MediaUsageResponse[]>();
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;

        void Promise.all(assets.map(asset => mediaLibraryService.details(asset.id ?? '')))
            .then(results => {
                if (active) setUsage(results.flatMap(result => result.usage ?? []));
            })
            .catch((failure: unknown) => {
                if (active) setError(getErrorMessage(failure, 'Не удалось проверить использование'));
            });

        return () => { active = false; };
    }, [assets]);

    /** Удаляет файлы после повторной серверной проверки */
    const remove = async (): Promise<void> => {
        setBusy(true);

        try {
            await mediaLibraryService.remove(assets.map(item => ({
                id: item.id,
                version: item.version
            })));

            onDeleted();
        } catch (failure: unknown) {
            setError(getErrorMessage(failure, 'Не удалось удалить файлы'));
        } finally {
            setBusy(false);
        }
    };

    const blocked = Boolean(usage?.length);

    return <Modal
        appearance="surface"
        visible
        title={blocked
            ? 'Нельзя удалить используемый файл'
            : 'Удалить из медиатеки?'
        }
        onHide={onClose}
        closable={!busy}
    >
        <div className={classes.dialogForm}>
            <div className={classes.warningSymbol}>
                <i
                    aria-hidden="true"
                    className={blocked
                        ? 'pi pi-lock'
                        : 'pi pi-trash'
                    }
                />
            </div>
            {!usage && !error &&
                <RequestLoading message="Проверяем использование изображений…" />
            }
            {usage &&
                <p>
                    {blocked
                        ? 'Сначала замените изображение в связанных объектах:'
                        : `Выбрано файлов: ${assets.length}. Они исчезнут из каталога. Оригиналы S3 сохранятся: существующие ссылки продолжат работать.`}
                </p>
            }
            {usage?.map((item, index) => <Link
                className={classes.usageLink}
                to={item.route ?? ''}
                key={`${item.id}-${index}`}
            >
                <i
                    aria-hidden="true"
                    className="pi pi-link"
                />
                <div>
                    <strong>
                        {item.name}
                    </strong>
                    <small>
                        {item.kind}
                    </small>
                </div>
            </Link>)}
            {error &&
                <RequestError message={error} />
            }
            <div className={classes.dialogFooter}>
                <ActionButton
                    type="button"
                    outlined
                    color="secondary"
                    onClick={onClose}
                    disabled={busy}
                >
                    {blocked
                        ? 'Понятно'
                        : 'Отмена'}
                </ActionButton>
                {!blocked &&
                    <ActionButton
                        type="button"
                        color="danger"
                        disabled={!usage || Boolean(error)}
                        loading={busy}
                        onClick={() => void remove()}
                    >
                        Удалить из каталога
                    </ActionButton>
                }
            </div>
        </div>
    </Modal>;
};
